import React from 'react';
import {
  FileText,
  Image,
  MessageSquare,
  Package,
  FolderTree,
  HelpCircle,
  Award,
  Search,
  Plus,
  RefreshCw,
} from 'lucide-react';

const EmptyState = ({
  type = 'default',
  title,
  description,
  action,
  illustration,
}) => {
  const getTypeConfig = () => {
    switch (type) {
      case 'gallery':
        return {
          icon: <Image size={64} />,
          defaultTitle: 'Belum Ada Foto',
          defaultDescription: 'Mulai dengan mengupload foto galeri henna terbaik Anda',
          actionText: 'Tambah Foto',
        };
      case 'testimonials':
        return {
          icon: <MessageSquare size={64} />,
          defaultTitle: 'Belum Ada Testimoni',
          defaultDescription: 'Testimoni dari klien akan membantu meningkatkan kepercayaan',
          actionText: 'Tambah Testimoni',
        };
      case 'packages':
        return {
          icon: <Package size={64} />,
          defaultTitle: 'Belum Ada Paket',
          defaultDescription: 'Buat paket layanan henna dengan harga dan fitur yang menarik',
          actionText: 'Tambah Paket',
        };
      case 'categories':
        return {
          icon: <FolderTree size={64} />,
          defaultTitle: 'Belum Ada Kategori',
          defaultDescription: 'Kategori akan membantu mengelola paket layanan',
          actionText: 'Tambah Kategori',
        };
      case 'faq':
        return {
          icon: <HelpCircle size={64} />,
          defaultTitle: 'Belum Ada FAQ',
          defaultDescription: 'FAQ akan membantu menjawab pertanyaan umum dari klien',
          actionText: 'Tambah FAQ',
        };
      case 'certificates':
        return {
          icon: <Award size={64} />,
          defaultTitle: 'Belum Ada Sertifikat',
          defaultDescription: 'Tampilkan sertifikat dan penghargaan Anda',
          actionText: 'Tambah Sertifikat',
        };
      case 'search':
        return {
          icon: <Search size={64} />,
          defaultTitle: 'Tidak Ditemukan',
          defaultDescription: 'Coba kata kunci lain atau periksa typo',
          actionText: null,
        };
      case 'bookings':
        return {
          icon: <FileText size={64} />,
          defaultTitle: 'Belum Ada Booking',
          defaultDescription: 'Booking yang masuk akan tampil di sini',
          actionText: null,
        };
      default:
        return {
          icon: <FileText size={64} />,
          defaultTitle: 'Tidak Ada Data',
          defaultDescription: 'Belum ada data yang tersedia',
          actionText: 'Tambah Data',
        };
    }
  };

  const config = getTypeConfig();

  return (
    <div className="text-center py-16 px-4">
      {/* Icon or Illustration */}
      <div className="mb-6 flex justify-center">
        {illustration || (
          <div className="relative">
            <div className="absolute inset-0 bg-[#D4AF7A]/10 rounded-full blur-3xl scale-150" />
            <div className="relative text-[#D4AF7A]/30">
              {config.icon}
            </div>
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-[#F7F5F2] mb-2">
        {title || config.defaultTitle}
      </h3>

      {/* Description */}
      <p className="text-[#F7F5F2]/70 max-w-md mx-auto mb-8">
        {description || config.defaultDescription}
      </p>

      {/* Action Button */}
      {action && config.actionText && (
        <button
          onClick={action.onClick}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black font-semibold rounded-lg hover:shadow-lg hover:shadow-[#D4AF7A]/30 transition-all"
        >
          {action.icon || <Plus size={20} />}
          {action.label || config.actionText}
        </button>
      )}
    </div>
  );
};

// Specialized Empty State Components
export const GalleryEmptyState = ({ onAdd }) => (
  <EmptyState
    type="gallery"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const TestimonialsEmptyState = ({ onAdd }) => (
  <EmptyState
    type="testimonials"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const PackagesEmptyState = ({ onAdd }) => (
  <EmptyState
    type="packages"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const CategoriesEmptyState = ({ onAdd }) => (
  <EmptyState
    type="categories"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const FAQEmptyState = ({ onAdd }) => (
  <EmptyState
    type="faq"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const CertificatesEmptyState = ({ onAdd }) => (
  <EmptyState
    type="certificates"
    action={onAdd ? { onClick: onAdd, icon: <Plus size={20} /> } : undefined}
  />
);

export const SearchEmptyState = ({ onClear }) => (
  <EmptyState
    type="search"
    action={
      onClear
        ? {
            onClick: onClear,
            label: 'Hapus Pencarian',
            icon: <RefreshCw size={20} />,
          }
        : undefined
    }
  />
);

// Error State Component
export const ErrorState = ({
  title = 'Terjadi Kesalahan',
  message = 'Gagal memuat data. Silakan coba lagi.',
  onRetry,
}) => (
  <div className="text-center py-16 px-4">
    <div className="mb-6 flex justify-center">
      <div className="relative">
        <div className="absolute inset-0 bg-red-500/10 rounded-full blur-3xl scale-150" />
        <div className="relative text-red-500/30">
          <RefreshCw size={64} />
        </div>
      </div>
    </div>

    <h3 className="text-xl font-semibold text-[#F7F5F2] mb-2">{title}</h3>
    <p className="text-[#F7F5F2]/70 max-w-md mx-auto mb-8">{message}</p>

    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-6 py-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/20 transition-all"
      >
        <RefreshCw size={20} />
        Coba Lagi
      </button>
    )}
  </div>
);

export default EmptyState;
